/**
 * Centralised date/time rendering. Every place this app shows a date or time to a person goes
 * through one of the two functions below, never an ad hoc `toLocaleDateString()` call scattered
 * per component.
 *
 * **This is not yet the per-user format preference `claude-kit/rules/invariants.md` requires**
 * ("Every displayed date or time gets a user-configurable format" — date-component order,
 * separator, and 12- vs. 24-hour time as a per-user setting). There is no settings surface for any
 * of that anywhere in this app yet, and the patient chart this module was built for
 * (`routes/patients/`) is the *first* UI surface that renders a date or time at all —
 * `OverviewPage` and `AccessPage` show none. Routing every call through these two functions is what
 * makes that follow-up cheap once it lands: a preference hook replaces the body of
 * `formatCalendarDate`/`formatDateTime` in one place, instead of a find-and-replace across every
 * table column and detail row that shows a date. Left as a named, deliberate gap — the task this
 * module was written for scoped to the patient list, chart, and note timeline, not a
 * workspace-wide settings feature; see the session report for the explicit callout.
 */

/** `YYYY-MM-DD` (`shared/src/clinicalRequests.ts`'s `calendarDateSchema`) → a locale-formatted date
 * with no time-of-day component. Falls back to the raw string for anything that fails to parse —
 * a display glitch is safer than a component that throws on data this build did not expect. */
export function formatCalendarDate(isoDate: string): string {
  const parsed = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return isoDate;
  return parsed.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/** An ISO 8601 datetime with offset → a locale-formatted date and time. Same fallback as above. */
export function formatDateTime(isoDateTime: string): string {
  const parsed = new Date(isoDateTime);
  if (Number.isNaN(parsed.getTime())) return isoDateTime;
  return parsed.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}
